import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, SlidersHorizontal, ChevronRight, History as HistoryIcon } from "lucide-react";
import { categoriaPuntuacion } from "@/lib/tastingOptions";

const ALL = "__all__";

export default function History() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [pais, setPais] = useState(ALL);
  const [variedad, setVariedad] = useState(ALL);
  const [minScore, setMinScore] = useState("");
  const [status, setStatus] = useState(ALL);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const { data: paises = [] } = useQuery({ queryKey: ["cat", "paises"], queryFn: async () => (await api.get("/catalogs/paises")).data });
  const { data: variedades = [] } = useQuery({ queryKey: ["cat", "variedades"], queryFn: async () => (await api.get("/catalogs/variedades")).data });

  const params = {
    search: search || undefined,
    pais: pais !== ALL ? pais : undefined,
    variedad: variedad !== ALL ? variedad : undefined,
    min_score: minScore || undefined,
    status: status !== ALL ? status : undefined,
    fecha_desde: fechaDesde || undefined,
    fecha_hasta: fechaHasta || undefined,
  };

  const { data: tastings = [], isLoading } = useQuery({
    queryKey: ["tastings", params],
    queryFn: async () => (await api.get("/tastings", { params })).data,
  });

  return (
    <div className="animate-fade-up">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-dorado-700 mb-1">Registro</p>
        <h1 className="font-serif text-4xl text-negro">Historial de Catas</h1>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-grises-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="history-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por vino o bodega..." className="pl-9 h-11" />
        </div>
        <Button data-testid="toggle-filters-btn" variant="outline" onClick={() => setShowFilters((s) => !s)}
          className="rounded-full border-grises-300 h-11">
          <SlidersHorizontal className="w-4 h-4 mr-1" /> Filtros
        </Button>
      </div>

      {showFilters && (
        <Card className="p-5 border-grises-200 rounded-xl mb-6 grid grid-cols-1 sm:grid-cols-4 gap-4 animate-fade-up">
          <div>
            <label className="text-xs text-grises-500 mb-1 block">País</label>
            <Select value={pais} onValueChange={setPais}>
              <SelectTrigger data-testid="filter-pais" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>Todos</SelectItem>{paises.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-grises-500 mb-1 block">Variedad</label>
            <Select value={variedad} onValueChange={setVariedad}>
              <SelectTrigger data-testid="filter-variedad" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>Todas</SelectItem>{variedades.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-grises-500 mb-1 block">Puntuación mínima</label>
            <Input data-testid="filter-score" type="number" value={minScore} onChange={(e) => setMinScore(e.target.value)} placeholder="0" className="h-10" />
          </div>
          <div>
            <label className="text-xs text-grises-500 mb-1 block">Estado</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="filter-status" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos</SelectItem>
                <SelectItem value="completa">Completa</SelectItem>
                <SelectItem value="borrador">Borrador</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-grises-500 mb-1 block">Fecha desde</label>
            <Input data-testid="filter-fecha-desde" type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="h-10" />
          </div>
          <div>
            <label className="text-xs text-grises-500 mb-1 block">Fecha hasta</label>
            <Input data-testid="filter-fecha-hasta" type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="h-10" />
          </div>
          {(fechaDesde || fechaHasta) && (
            <div className="flex items-end">
              <Button data-testid="clear-fecha-btn" variant="outline" onClick={() => { setFechaDesde(""); setFechaHasta(""); }} className="h-10 rounded-full border-grises-300 text-sm">
                Limpiar fechas
              </Button>
            </div>
          )}
        </Card>
      )}

      {isLoading ? (
        <p className="text-grises-400">Cargando...</p>
      ) : tastings.length === 0 ? (
        <Card className="p-12 text-center border-grises-200 rounded-xl">
          <HistoryIcon className="w-12 h-12 text-borgona-300 mx-auto mb-3" strokeWidth={1.25} />
          <p className="text-grises-500">No se encontraron catas.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {tastings.map((t) => {
            const cat = categoriaPuntuacion(t.puntuacion);
            return (
              <Card key={t.id} data-testid={`history-item-${t.id}`}
                onClick={() => navigate(`/app/catas/${t.id}`)}
                className="p-5 border-grises-200 rounded-xl flex items-center gap-4 cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_6px_24px_rgba(0,0,0,0.06)] transition-transform duration-300">
                <div className="w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 font-mono font-bold text-blanco" style={{ background: cat.color }}>
                  <span className="text-xl leading-none">{t.puntuacion}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-serif text-lg text-negro truncate">{t.wine?.nombre || "Vino"}</h3>
                    {t.status === "borrador" && <span className="text-xs px-2 py-0.5 rounded-full bg-grises-100 text-grises-500 shrink-0">Borrador</span>}
                  </div>
                  <p className="text-sm text-grises-500 truncate">{[t.wine?.bodega, t.wine?.pais, t.wine?.variedad, t.wine?.anada].filter(Boolean).join(" · ")}</p>
                  <p className="text-xs mt-0.5 flex items-center gap-2" style={{ color: cat.color }}>
                    {cat.label}
                    {t.fecha_cata && <span className="text-grises-400">· {t.fecha_cata}</span>}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-grises-300 shrink-0" />
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
